import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { colors } from '@sweep-io/sweep-design';
import { ConfigurationType } from './types';
import uniq from 'lodash/uniq';
import { OpenAiResponse, OpenAiDescription, ConfigurationItem } from '../../parser/ParserTypes';
import { readableUUID } from '../../../navigation-utils/readableUUID';
import { getDataFromRecordTypeIdentifier, getDataFromStepId } from '../pills/pillsUtils';
import {
  FIELD,
  APEX_CLASS,
  FLOW,
  PROCESS_BUILDER_FLOW,
  APEX_TRIGGER,
  LAYOUT,
  APPROVAL_PROCESS,
  VALIDATION_RULE,
  WORKFLOW_RULE,
  EMAIL_TEMPLATE,
  RECORD_TYPE,
  REPORT,
} from '@server/parser';

export const SectionBox = styled(Box)(() => ({
  padding: '0 0 16px 0',
  borderBottom: `1px solid ${colors.grey[120]}`,
  width: '100%',
}));

export const HEADER_TOP_ENDORNMENT = '28px';

export const SWEEP_ELEMENTS = [
  ConfigurationType.sweepAlert,
  ConfigurationType.sweepDedupe,
  ConfigurationType.sweepAutomation,
  ConfigurationType.sweepMatching,
  ConfigurationType.sweepAssignment,
  ConfigurationType.sweepScheduledAssignment,
  ConfigurationType.rollups,
];

export const excludedFromDetails = [
  ConfigurationType.apexClasses,
  ConfigurationType.apexTriggers,
  ConfigurationType.cpqData,
  ConfigurationType.objects,
  ConfigurationType.recordTypes,
  ...SWEEP_ELEMENTS.filter((item) => item !== ConfigurationType.rollups),
];

export const isFieldType = (itemType: string) =>
  itemType === ConfigurationType.fields || itemType === ConfigurationType.rollups;

export const formatOpenAiResponse = (res: OpenAiResponse): OpenAiDescription => {
  const isError = Boolean(res.error);
  return {
    type: isError ? 'error' : 'data',
    text: (res.data as string) || 'Could not load content at this time',
  };
};

export const getChips = (
  configurationItem: ConfigurationItem,
  stepNamesUsedInCanvas: string[],
  saltoObjectTypes: Pick<ObjectTypeName, 'objectType' | 'label'>[],
) => {
  const { objectApiNames, stagesNames, usedByRecordTypes } = configurationItem ?? {};

  const objectLabels = objectApiNames?.map(
    (name) => saltoObjectTypes?.find((type) => type.objectType === name)?.label ?? name,
  );

  const stageNamesFromId = uniq(
    stagesNames
      ?.filter(
        (stageName) =>
          stageName &&
          stepNamesUsedInCanvas.some(
            (stepName) =>
              getDataFromStepId(stepName).stepFullName ===
              getDataFromStepId(stageName).stepFullName,
          ),
      )
      ?.map((name) => getDataFromStepId(name).stepFullName),
  );

  const stageNameChips = stageNamesFromId?.length ? stageNamesFromId : [];
  const objectChips = objectLabels && objectLabels?.length > 1 ? objectLabels : [];
  const RTchips = usedByRecordTypes?.length
    ? uniq(
        usedByRecordTypes
          ?.filter((rt) => !!rt)
          .map((rt) => getDataFromRecordTypeIdentifier(rt).recordTypeName),
      )
    : [];
  const chips =
    stageNameChips.length || RTchips.length ? [...stageNameChips, ...RTchips] : [...objectChips];

  return { chips, RTchips };
};

export const buildConfigurationItemId = (item: {
  parentType: string;
  name: string;
  id?: string;
}) => {
  return (
    item.parentType +
    '|' +
    item.name +
    (item.id && readableUUID(item.id) ? '|' + readableUUID(item.id) : '')
  );
};

export const translateNewConfigurationTypeKeysToOldVersion = (type: string): ConfigurationType => {
  switch (type) {
    case APEX_CLASS:
      return ConfigurationType.apexClasses;
    case APEX_TRIGGER:
      return ConfigurationType.apexTriggers;
    case APPROVAL_PROCESS:
      return ConfigurationType.approvalProcesses;
    case EMAIL_TEMPLATE:
      return ConfigurationType.emailTemplates;
    case FIELD:
      return ConfigurationType.fields;
    case FLOW:
      return ConfigurationType.flows;
    case PROCESS_BUILDER_FLOW:
      return ConfigurationType.processBuilderFlows;
    case LAYOUT:
      return ConfigurationType.layouts;
    case REPORT:
      return ConfigurationType.reports;
    case VALIDATION_RULE:
      return ConfigurationType.validationRules;
    case WORKFLOW_RULE:
      return ConfigurationType.workflowRules;
    case RECORD_TYPE:
      return ConfigurationType.recordTypes;
    case 'Object': //for metadata elements in canvas
      return ConfigurationType.objects;
    default:
      return type as ConfigurationType;
  }
};
